/**
 * CLI Credential Template Commands
 *
 * Manage property-scoped credential templates (logical credential identities
 * that group per-environment concrete credential bindings).
 *
 * @module credential-template
 */

import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest, fetchProperties, getApplicationArg } from './cli-helpers.js';

type PersonaVariableDefinition = {
  name: string;
  description?: string;
  sensitive?: boolean;
};

type Template = {
  id: string;
  propertyId: string;
  name: string;
  description: string | null;
  variableDefinitions: PersonaVariableDefinition[] | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * Parse repeatable `--variable NAME[:sensitive]` flags into persona variable
 * definitions. Examples: `--variable ACCOUNT_ID`, `--variable API_TOKEN:sensitive`.
 */
function parseVariableDefinitions(argv: string[]): PersonaVariableDefinition[] {
  const defs: PersonaVariableDefinition[] = [];
  for (let i = 0; i < argv.length; i++) {
    if (argv[i] !== '--variable') continue;
    const raw = argv[i + 1];
    if (!raw || raw.startsWith('-')) continue;
    const [name, modifier] = raw.split(':');
    if (!name) continue;
    defs.push({ name, ...(modifier === 'sensitive' ? { sensitive: true } : {}) });
  }
  return defs;
}

type Binding = {
  id: string;
  name: string;
  environmentId: string | null;
  isEphemeral: boolean;
  templateId: string | null;
};

function printHelp(): void {
  console.log(
    [
      'Usage: canary persona <sub-command> [options]',
      '  (alias: canary credential-template — kept for backwards compatibility)',
      '',
      'Sub-commands: (--application accepted as --app; --property is a backwards-compat alias)',
      '  list --application <name|id>           List personas on an application',
      '  create --application <name|id> --name <name> [--description <text>] [--variable NAME[:sensitive]]...',
      '  show --application <name|id> <personaId>  Show persona + its bindings',
      '  rename --application <name|id> <personaId> --name <new-name>',
      '  delete --application <name|id> <personaId>',
      '  link --application <name|id> <personaId> --credential <credId>',
      '  unlink --application <name|id> <personaId> --credential <credId>',
      '  variable list   --application <name|id> <personaId>',
      '  variable add    --application <name|id> <personaId> NAME[:sensitive] [--description <text>] [--sensitive]',
      '  variable remove --application <name|id> <personaId> NAME [--force]',
      '',
      'Persona variables are declared here and filled per-credential; injected at run time',
      'as $var.USER.<NAME>. Removing an in-use variable requires --force.',
      '',
      'Common options:',
      '  --json               Output raw JSON',
      '  --env <env>          Target environment (prod, dev, local)',
      '  --api-url <url>      API URL override',
      '  --token <key>        API token override',
    ].join('\n')
  );
}

/**
 * Return the first positional argument, excluding values consumed by the given
 * `--flag value` pairs. Prior implementation used `argv.find(a => !a.startsWith('-'))`
 * which incorrectly returned the value of `--property` (the first flag value) as
 * the templateId.
 */
function getPositionalArg(argv: string[], flagsWithValues: string[]): string | undefined {
  const skip = new Set<number>();
  for (let i = 0; i < argv.length; i++) {
    if (flagsWithValues.includes(argv[i]!) && i + 1 < argv.length) {
      skip.add(i);
      skip.add(i + 1);
    }
  }
  for (let i = 0; i < argv.length; i++) {
    if (skip.has(i)) continue;
    const a = argv[i]!;
    if (!a.startsWith('-')) return a;
  }
  return undefined;
}

/** All positional args, excluding values consumed by the given `--flag value` pairs. */
function getPositionals(argv: string[], flagsWithValues: string[]): string[] {
  const skip = new Set<number>();
  for (let i = 0; i < argv.length; i++) {
    if (flagsWithValues.includes(argv[i]!) && i + 1 < argv.length) {
      skip.add(i);
      skip.add(i + 1);
    }
  }
  const out: string[] = [];
  for (let i = 0; i < argv.length; i++) {
    if (skip.has(i)) continue;
    const a = argv[i]!;
    if (!a.startsWith('-')) out.push(a);
  }
  return out;
}

const TEMPLATE_FLAGS = [
  '--application',
  '--property',
  '--credential',
  '--name',
  '--description',
  '--variable',
  '--env',
  '--api-url',
  '--token',
];

async function resolveProperty(
  apiUrl: string,
  token: string,
  arg: string
): Promise<{ id: string; name: string }> {
  const properties = await fetchProperties(apiUrl, token);
  const property = properties.find(
    (p) => p.id === arg || p.name.toLowerCase() === arg.toLowerCase()
  );
  if (!property) {
    console.error(`Error: Application "${arg}" not found.`);
    process.exit(1);
  }
  return property;
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyArg = getApplicationArg(argv);
  const jsonFlag = hasFlag(argv, '--json');
  if (!propertyArg) {
    console.error('Error: --application is required (alias: --property).');
    process.exit(1);
  }
  const property = await resolveProperty(apiUrl, token, propertyArg);
  const result = await apiRequest<{ ok: boolean; items?: Template[]; error?: string }>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${property.id}/credential-templates`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Failed to list personas'}`);
    process.exit(1);
  }
  const items = result.items ?? [];
  if (jsonFlag) {
    console.log(JSON.stringify(items, null, 2));
    return;
  }
  if (items.length === 0) {
    console.log(`No personas on application "${property.name}".`);
    return;
  }
  for (const t of items) {
    console.log(`${t.id}  ${t.name}${t.description ? ` — ${t.description}` : ''}`);
  }
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyArg = getApplicationArg(argv);
  const name = getArgValue(argv, '--name');
  const description = getArgValue(argv, '--description');
  const jsonFlag = hasFlag(argv, '--json');

  if (!propertyArg || !name) {
    console.error('Error: --application and --name are required.');
    process.exit(1);
  }
  const property = await resolveProperty(apiUrl, token, propertyArg);
  const variableDefinitions = parseVariableDefinitions(argv);
  const result = await apiRequest<{ ok: boolean; template?: Template; error?: string }>(
    apiUrl,
    token,
    'POST',
    `/org/properties/${property.id}/credential-templates`,
    {
      name,
      description: description ?? null,
      ...(variableDefinitions.length > 0 ? { variableDefinitions } : {}),
    }
  );
  if (!result.ok || !result.template) {
    console.error(`Error: ${result.error ?? 'Failed to create persona'}`);
    process.exit(1);
  }
  if (jsonFlag) {
    console.log(JSON.stringify(result.template, null, 2));
    return;
  }
  console.log(`Created persona "${result.template.name}" (${result.template.id})`);
}

async function handleShow(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyArg = getApplicationArg(argv);
  const templateId = getPositionalArg(argv, TEMPLATE_FLAGS);
  const jsonFlag = hasFlag(argv, '--json');

  if (!propertyArg || !templateId) {
    console.error('Error: --application and <personaId> are required.');
    process.exit(1);
  }
  const property = await resolveProperty(apiUrl, token, propertyArg);
  const result = await apiRequest<{
    ok: boolean;
    template?: Template;
    bindings?: Binding[];
    error?: string;
  }>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${property.id}/credential-templates/${templateId}`
  );
  if (!result.ok || !result.template) {
    console.error(`Error: ${result.error ?? 'Failed to fetch persona'}`);
    process.exit(1);
  }
  if (jsonFlag) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }
  const t = result.template;
  console.log(`${t.name} (${t.id})`);
  if (t.description) console.log(`  ${t.description}`);
  console.log(`Bindings (${result.bindings?.length ?? 0}):`);
  for (const b of result.bindings ?? []) {
    const env = b.environmentId ?? '(no env)';
    const eph = b.isEphemeral ? ' [ephemeral]' : '';
    console.log(`  ${b.id}  env=${env}  ${b.name}${eph}`);
  }
}

async function handleRename(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyArg = getApplicationArg(argv);
  const newName = getArgValue(argv, '--name');
  const templateId = getPositionalArg(argv, TEMPLATE_FLAGS);
  if (!propertyArg || !templateId || !newName) {
    console.error('Error: --application, <personaId>, and --name are required.');
    process.exit(1);
  }
  const property = await resolveProperty(apiUrl, token, propertyArg);
  const result = await apiRequest<{ ok: boolean; template?: Template; error?: string }>(
    apiUrl,
    token,
    'PATCH',
    `/org/properties/${property.id}/credential-templates/${templateId}`,
    { name: newName }
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Failed to rename persona'}`);
    process.exit(1);
  }
  console.log(`Renamed persona ${templateId} → "${newName}"`);
}

async function handleDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyArg = getApplicationArg(argv);
  const templateId = getPositionalArg(argv, TEMPLATE_FLAGS);
  if (!propertyArg || !templateId) {
    console.error('Error: --application and <personaId> are required.');
    process.exit(1);
  }
  const property = await resolveProperty(apiUrl, token, propertyArg);
  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'DELETE',
    `/org/properties/${property.id}/credential-templates/${templateId}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Failed to delete persona'}`);
    process.exit(1);
  }
  console.log(`Deleted persona ${templateId}`);
}

async function handleLink(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyArg = getApplicationArg(argv);
  const credentialId = getArgValue(argv, '--credential');
  const templateId = getPositionalArg(argv, TEMPLATE_FLAGS);
  if (!propertyArg || !templateId || !credentialId) {
    console.error('Error: --application, <personaId>, and --credential are required.');
    process.exit(1);
  }
  const property = await resolveProperty(apiUrl, token, propertyArg);
  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'POST',
    `/org/properties/${property.id}/credential-templates/${templateId}/bindings`,
    { credentialId }
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Failed to link credential'}`);
    process.exit(1);
  }
  console.log(`Linked credential ${credentialId} to persona ${templateId}`);
}

async function handleUnlink(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyArg = getApplicationArg(argv);
  const credentialId = getArgValue(argv, '--credential');
  const templateId = getPositionalArg(argv, TEMPLATE_FLAGS);
  if (!propertyArg || !templateId || !credentialId) {
    console.error('Error: --application, <personaId>, and --credential are required.');
    process.exit(1);
  }
  const property = await resolveProperty(apiUrl, token, propertyArg);
  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'DELETE',
    `/org/properties/${property.id}/credential-templates/${templateId}/bindings/${credentialId}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Failed to unlink credential'}`);
    process.exit(1);
  }
  console.log(`Unlinked credential ${credentialId} from persona ${templateId}`);
}

async function fetchTemplate(
  apiUrl: string,
  token: string,
  propertyId: string,
  templateId: string
): Promise<Template> {
  const result = await apiRequest<{ ok: boolean; template?: Template; error?: string }>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/credential-templates/${templateId}`
  );
  if (!result.ok || !result.template) {
    console.error(`Error: ${result.error ?? 'Failed to fetch persona'}`);
    process.exit(1);
  }
  return result.template;
}

async function patchVariableDefinitions(
  apiUrl: string,
  token: string,
  propertyId: string,
  templateId: string,
  variableDefinitions: PersonaVariableDefinition[],
  force: boolean
): Promise<void> {
  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'PATCH',
    `/org/properties/${propertyId}/credential-templates/${templateId}`,
    { variableDefinitions, ...(force ? { force: true } : {}) }
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Failed to update persona variables'}`);
    process.exit(1);
  }
}

/**
 * `persona variable add|list|remove <personaId> [NAME[:sensitive]]`
 * Declare / inspect / remove the variables a persona carries (credentials fill the values;
 * injected at run time as `$var.USER.<name>`).
 */
async function handleVariable(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [action, ...rest] = argv;
  const propertyArg = getApplicationArg(rest);
  const jsonFlag = hasFlag(rest, '--json');
  const positionals = getPositionals(rest, TEMPLATE_FLAGS);
  const personaId = positionals[0];

  if (!action || !['add', 'list', 'remove'].includes(action)) {
    console.error('Error: persona variable <add|list|remove> …');
    process.exit(1);
  }
  if (!propertyArg || !personaId) {
    console.error('Error: --application and <personaId> are required.');
    process.exit(1);
  }
  const property = await resolveProperty(apiUrl, token, propertyArg);

  if (action === 'list') {
    const template = await fetchTemplate(apiUrl, token, property.id, personaId);
    const defs = template.variableDefinitions ?? [];
    if (jsonFlag) {
      console.log(JSON.stringify(defs, null, 2));
      return;
    }
    if (defs.length === 0) {
      console.log(`No variables declared on persona "${template.name}".`);
      return;
    }
    for (const d of defs) {
      const sens = d.sensitive ? ' [sensitive]' : '';
      console.log(`${d.name}${sens}${d.description ? ` — ${d.description}` : ''}`);
    }
    return;
  }

  // add / remove both need the variable name (NAME[:sensitive] for add).
  const spec = positionals[1];
  if (!spec) {
    console.error(`Error: persona variable ${action} <personaId> NAME is required.`);
    process.exit(1);
  }
  const [name, modifier] = spec.split(':');
  if (!name) {
    console.error('Error: variable NAME is required.');
    process.exit(1);
  }
  const template = await fetchTemplate(apiUrl, token, property.id, personaId);
  const existing = template.variableDefinitions ?? [];

  if (action === 'add') {
    const description = getArgValue(rest, '--description');
    const def: PersonaVariableDefinition = {
      name,
      ...(modifier === 'sensitive' || hasFlag(rest, '--sensitive') ? { sensitive: true } : {}),
      ...(description ? { description } : {}),
    };
    // Replace an existing definition with the same name, else append.
    const next = existing.some((d) => d.name === name)
      ? existing.map((d) => (d.name === name ? def : d))
      : [...existing, def];
    await patchVariableDefinitions(apiUrl, token, property.id, personaId, next, false);
    console.log(`Added variable "${name}" to persona ${personaId}`);
    return;
  }

  // remove
  if (!existing.some((d) => d.name === name)) {
    console.error(`Error: persona has no variable "${name}".`);
    process.exit(1);
  }
  const next = existing.filter((d) => d.name !== name);
  await patchVariableDefinitions(
    apiUrl,
    token,
    property.id,
    personaId,
    next,
    hasFlag(rest, '--force')
  );
  console.log(`Removed variable "${name}" from persona ${personaId}`);
}

export async function runCredentialTemplate(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(rest);

  switch (subCommand) {
    case 'list':
      await handleList(rest, apiUrl, token);
      return;
    case 'create':
      await handleCreate(rest, apiUrl, token);
      return;
    case 'show':
      await handleShow(rest, apiUrl, token);
      return;
    case 'rename':
      await handleRename(rest, apiUrl, token);
      return;
    case 'delete':
      await handleDelete(rest, apiUrl, token);
      return;
    case 'link':
      await handleLink(rest, apiUrl, token);
      return;
    case 'unlink':
      await handleUnlink(rest, apiUrl, token);
      return;
    case 'variable':
    case 'variables':
      await handleVariable(rest, apiUrl, token);
      return;
    default:
      console.error(`Unknown persona sub-command: ${subCommand}`);
      printHelp();
      process.exit(1);
  }
}
